import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { attachmentList } from './fileAttachment';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Divider,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Paper,
    Grid,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Description,
    AttachFile,
    Edit,
    CheckCircle,
    Warning,
    Visibility,
    Download
} from '@mui/icons-material';
import styles from './Decloration.module.css';

const MANDATORY_FILE_NAMES = ['Чек ККТ', 'Договор/спортивный абонемент'];

const getDisplayLabel = (field) => {
    const raw = field.label || field.name;
    return raw === 'БСО' ? 'БСО (при наличии)' : raw;
};

const isReviewFieldRequired = (field) => {
    const isFile = field.field_type.type === 'file';
    if (isFile) {
        const label = getDisplayLabel(field);
        return MANDATORY_FILE_NAMES.includes(label);
    }
    return true;
};

const DeclarationReview = ({ 
    fieldValues = {}, 
    applicationType, 
    onEdit, 
    onSubmit, 
    isSubmitting = false,
    submitError = null 
}) => {
    const navigate = useNavigate();
    const [previewFile, setPreviewFile] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    
    // Ensure fieldValues is always an object
    const safeFieldValues = fieldValues || {};

    /** Parent passes filtered blocks (same as mobile); count only visible field ids */
    const visibleFieldIds = useMemo(() => {
        const s = new Set();
        (applicationType?.active_field_blocks || []).forEach((block) => {
            (block.active_fields || []).forEach((f) => {
                if (f.id != null) {
                    s.add(String(f.id));
                }
            });
        });
        return s;
    }, [applicationType]);

    const filledVisibleCount = useMemo(
        () =>
            Object.keys(safeFieldValues).filter((k) => visibleFieldIds.has(String(k)))
                .length,
        [safeFieldValues, visibleFieldIds],
    );

    const totalVisibleFields = useMemo(
        () =>
            (applicationType?.active_field_blocks || []).reduce(
                (acc, block) => acc + (block.active_fields?.length || 0),
                0,
            ),
        [applicationType],
    );

    const handlePreviewFile = (file) => {
        setPreviewFile(file);
        setPreviewDialogOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
        setPreviewFile(null);
    };

    const renderOneFile = (field, item) => {
        if (typeof item === 'object' && item.isExisting && item.url) {
            return (
                <Box
                    key={item.url}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            flexGrow: 1,
                        }}
                    >
                        <AttachFile color="primary" />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                minWidth: 0,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={item.name || 'Прикрепленный файл'}
                        >
                            {item.name || 'Прикрепленный файл'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => window.open(item.url, '_blank')}
                            title="Открыть файл"
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                        <Chip
                            label="Уже прикреплен"
                            size="small"
                            color="info"
                            icon={<CheckCircle />}
                        />
                    </Box>
                </Box>
            );
        }
        if (item instanceof File) {
            return (
                <Box
                    key={item.name + item.size}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            flexGrow: 1,
                        }}
                    >
                        <AttachFile color="primary" />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                minWidth: 0,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={item.name}
                        >
                            {item.name}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => handlePreviewFile(item)}
                            title="Предпросмотр"
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                        <Chip
                            label="Прикреплен"
                            size="small"
                            color="success"
                            icon={<CheckCircle />}
                        />
                    </Box>
                </Box>
            );
        }
        return null;
    };

    const renderFieldValue = (field, value) => {
        if (field.field_type.type === 'file' && value) {
            const list = Array.isArray(value) ? value : [value];
            if (list.length === 0) return null;
            if (list.length === 1) {
                return renderOneFile(field, list[0]);
            }
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {list.map((item, idx) => (
                        <Box key={idx}>{renderOneFile(field, item)}</Box>
                    ))}
                </Box>
            );
        }

        if (field.field_type.type === 'email') {
            return (
                <Typography variant="body2" color="primary">
                    {value}
                </Typography>
            );
        }

        if (field.field_type.type === 'tel') {
            return (
                <Typography variant="body2">
                    {value}
                </Typography>
            );
        }

        // Period field: display as "from - to" format
        if (field.field_type.type === 'period' && value) {
            try {
                const period = typeof value === 'string' ? JSON.parse(value) : value;
                if (period.from && period.to) {
                    // Format dates from YYYY-MM-DD to DD.MM.YYYY
                    const formatDate = (dateStr) => {
                        if (!dateStr) return '';
                        const [year, month, day] = dateStr.split('-');
                        return `${day}.${month}.${year}`;
                    };
                    return (
                        <Typography variant="body2">
                            {formatDate(period.from)} - {formatDate(period.to)}
                        </Typography>
                    );
                }
            } catch (e) {
                // Invalid JSON, show raw value
            }
            return (
                <Typography variant="body2" color="text.secondary">
                    Не заполнено
                </Typography>
            );
        }

        return (
            <Typography variant="body2">
                {value || 'Не заполнено'}
            </Typography>
        );
    };

    const getFieldValidationStatus = (field, value) => {
        const required = isReviewFieldRequired(field);

        // Period field validation
        if (field.field_type.type === 'period') {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                if (required) {
                    return { valid: false, message: 'Поле не заполнено' };
                }
                return { valid: true, message: 'Поле не заполнено (необязательно)' };
            }
            try {
                const period = typeof value === 'string' ? JSON.parse(value) : value;
                if (!period.from || !period.to) {
                    return { valid: false, message: 'Заполните обе даты периода' };
                }
                const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                if (!dateRegex.test(period.from) || !dateRegex.test(period.to)) {
                    return { valid: false, message: 'Неверный формат дат периода' };
                }
                if (new Date(period.from) > new Date(period.to)) {
                    return { valid: false, message: 'Дата начала не может быть позже даты окончания' };
                }
                return { valid: true, message: 'Период заполнен корректно' };
            } catch (e) {
                return { valid: false, message: 'Неверный формат периода' };
            }
        }

        // File field with multiple files
        if (field.field_type.type === 'file' && field.is_multiple) {
            const list = attachmentList(value);
            const hasValid = list.length > 0;
            if (required && !hasValid) {
                return { valid: false, message: 'Необходимо прикрепить хотя бы один файл' };
            }
            return { valid: true, message: list.length ? `Прикреплено файлов: ${list.length}` : 'Файлы не прикреплены (необязательно)' };
        }

        // Check if value is empty (handle both strings and file objects)
        const isEmpty = !value ||
            (typeof value === 'string' && value.trim() === '') ||
            (value instanceof File && !value.name) ||
            (typeof value === 'object' && value.isExisting && !value.url);

        if (isEmpty) {
            if (required) {
                return { valid: false, message: 'Поле не заполнено' };
            }
            return { valid: true, message: 'Поле не заполнено (необязательно)' };
        }

        if (field.field_type.type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return { valid: false, message: 'Неверный формат email' };
            }
        }

        if (field.field_type.type === 'tel' && value) {
            const phoneRegex = /^\+7 \(\d{3}\) \d{3} \d{2} \d{2}$/;
            if (!phoneRegex.test(value)) {
                return { valid: false, message: 'Неверный формат телефона' };
            }
        }

        return { valid: true, message: 'Поле заполнено корректно' };
    };

    const renderFieldSection = (fieldBlock, blockIndex) => {
        const fieldsList = fieldBlock?.active_fields || []
        if (fieldsList.length === 0) return null
        return (
            <Card key={blockIndex} sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#EF2E25' }}>
                        {fieldBlock.name || `Раздел ${blockIndex + 1}`}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                        {fieldsList.map((field, fieldIndex) => {
                            const value = safeFieldValues[field.id];
                            const required = isReviewFieldRequired(field);
                            const displayLabel = getDisplayLabel(field);
                            const validation = getFieldValidationStatus(field, value);
                            
                            return (
                                <Grid item xs={12} md={6} key={fieldIndex}>
                                    <Paper 
                                        elevation={1} 
                                        sx={{ 
                                            p: 2, 
                                            border: validation.valid ? '1px solid #e0e0e0' : '1px solid #ffcdd2',
                                            backgroundColor: validation.valid ? 'inherit' : '#fff3f3'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {displayLabel}
                                                {required && <span style={{ color: 'red' }}> *</span>}
                                            </Typography>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => onEdit(blockIndex)}
                                                title="Редактировать"
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        
                                        {renderFieldValue(field, value)}
                                        
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {validation.valid ? (
                                                <CheckCircle color="success" fontSize="small" />
                                            ) : (
                                                <Warning color="error" fontSize="small" />
                                            )}
                                            <Typography 
                                                variant="caption" 
                                                color={validation.valid ? 'success.main' : 'error.main'}
                                            >
                                                {validation.message}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const allFieldsValid = () => {
        if (!applicationType?.active_field_blocks) return false;
        
        return applicationType.active_field_blocks.every(block =>
            (block.active_fields || []).every(field => {
                const value = safeFieldValues[field.id];
                const validation = getFieldValidationStatus(field, value);
                return validation.valid;
            })
        );
    };

    const getAttachedFilesCount = () => {
        let count = 0;
        Object.values(safeFieldValues).forEach(value => {
            count += attachmentList(value).length;
        });
        return count;
    };

    // Guard against missing applicationType
    if (!applicationType || !applicationType.active_field_blocks) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                    Загрузка данных формы...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2, pb: { xs: '140px', md: 2 } }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ color: '#EF2E25' }}>
                Проверка заявления
            </Typography>
            
            <Typography variant="body1" gutterBottom align="center" color="text.secondary" sx={{ mb: 4 }}>
                Пожалуйста, проверьте правильность заполнения всех полей перед отправкой
            </Typography>

            {/* Summary Card */}
            <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#EF2E25' }}>
                        Сводка заявления
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">
                                Тип заявления:
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {applicationType?.name || 'Не указан'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">
                                Заполнено полей:
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {filledVisibleCount} из {totalVisibleFields}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">
                                Прикреплено файлов:
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {getAttachedFilesCount()}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Error Alert */}
            {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {submitError}
                </Alert>
            )}

            {/* Fields Review */}
            {applicationType.active_field_blocks && applicationType.active_field_blocks.length > 0 ? (
                applicationType.active_field_blocks.map((block, index) => 
                    renderFieldSection(block, index)
                )
            ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Нет полей для отображения
                </Alert>
            )}

            {/* File Preview Dialog */}
            <Dialog 
                open={previewDialogOpen} 
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Предварительный просмотр файла
                </DialogTitle>
                <DialogContent>
                    {previewFile && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {previewFile.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Размер: {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Тип: {previewFile.type}
                            </Typography>
                            
                            {/* File preview content would go here */}
                            <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Предварительный просмотр файла
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClosePreview}
                        sx={{
                            borderRadius: '32px',
                        }}
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
                <Button 
                    variant="outlined" 
                    onClick={() => navigate('/declarations')}
                    disabled={isSubmitting}
                    sx={{
                        borderRadius: '32px',
                        color: '#EF2E25',
                        borderColor: '#EF2E25',
                        '&:hover': {
                            borderColor: '#F36D52',
                            backgroundColor: 'rgba(239, 46, 37, 0.04)'
                        }
                    }}
                >
                    Отмена
                </Button>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        onClick={() => onEdit(0)}
                        disabled={isSubmitting}
                        startIcon={<Edit />}
                        sx={{
                            borderRadius: '32px',
                            color: '#EF2E25',
                            borderColor: '#EF2E25',
                            '&:hover': {
                                borderColor: '#F36D52',
                                backgroundColor: 'rgba(239, 46, 37, 0.04)'
                            }
                        }}
                    >
                        Редактировать
                    </Button>
                    
                    <Button 
                        variant="contained" 
                        onClick={onSubmit}
                        disabled={isSubmitting || !allFieldsValid()}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />}
                        sx={{
                            borderRadius: '32px',
                            backgroundColor: allFieldsValid() ? '#EF2E25' : '#F36D52',
                            '&:hover': {
                                backgroundColor: allFieldsValid() ? '#F36D52' : '#EF2E25'
                            },
                            '&:disabled': {
                                backgroundColor: '#ccc'
                            }
                        }}
                    >
                        {isSubmitting ? 'Отправка...' : 'Отправить заявление'}
                    </Button>
                </Box>
            </Box>

            {/* Validation Warning */}
            {!allFieldsValid() && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Пожалуйста, исправьте все ошибки перед отправкой заявления
                </Alert>
            )}
        </Box>
    );
};

export default DeclarationReview; 